import React, { useState } from 'react';
import { Palette, ChevronDown } from 'lucide-react';
import type { Theme } from '../config/themes';
import { themes } from '../config/themes';

interface ThemeSelectorProps {
  currentTheme: Theme;
  onThemeChange: (theme: Theme) => void;
  isDark: boolean;
}

/*
const themes: { id: Theme; name: string; primary: string; secondary: string }[] = [
  { id: 'emerald', name: 'Emerald', primary: 'bg-emerald-500', secondary: 'bg-emerald-100' },
  { id: 'blue', name: 'Blue', primary: 'bg-blue-500', secondary: 'bg-blue-100' },
  { id: 'purple', name: 'Purple', primary: 'bg-purple-500', secondary: 'bg-purple-100' },
  { id: 'amber', name: 'Amber', primary: 'bg-amber-500', secondary: 'bg-amber-100' }
];
*/

export function ThemeSelector({ currentTheme, onThemeChange, isDark }: ThemeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 p-2 rounded-lg ${
          isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
        } transition-colors`}
        title="Change theme"
      >
        <Palette className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className={`absolute top-full right-0 mt-2 w-48 rounded-lg shadow-lg border z-20 ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="p-2">
              <div className="text-sm font-medium mb-2 px-2 py-1">Choose Theme</div>
              <div className="grid grid-cols-2 gap-2">
                {themes.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => {
                      onThemeChange(theme.id);
                      setIsOpen(false);
                    }}
                    className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                      currentTheme === theme.id
                        ? isDark ? 'bg-gray-700' : 'bg-gray-100'
                        : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex gap-1">
                      <div className={`w-3 h-3 rounded-full ${theme.primary}`} />
                      <div className={`w-3 h-3 rounded-full ${theme.secondary}`} />
                    </div>
                    <span className="text-xs">{theme.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}